import React, { useMemo } from 'react'
import ReactEChartsCore from './EChartsCore.js'
import * as echarts from 'echarts/core'
import type { ComposeOption } from 'echarts/core'
import { PieChart, type PieSeriesOption } from 'echarts/charts'
import { LegendComponent, TooltipComponent, type LegendComponentOption, type TooltipComponentOption } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { visTickFormatter } from './Options/vizFormatters.js'
import { CHART_SURFACE, fillStyleFor, identityFor } from './Options/visualIdentity.js'

echarts.use([PieChart, LegendComponent, TooltipComponent, CanvasRenderer])

type EChartsOption = ComposeOption<PieSeriesOption | LegendComponentOption | TooltipComponentOption>

export interface PieSlice {
  name: string
  value: number
}

interface Props {
  data: PieSlice[]
  onSliceClick?: (name: string) => void
  height?: number | `${number}%`
}

/**
 * ECharts pie chart. Each slice wears its name's visual identity; clicking a slice reports its `name`.
 */
export const PieChartNext: React.FC<Props> = ({ data, onSliceClick, height }) => {
  const option: EChartsOption = useMemo(() => {
    const total = Math.max(
      1,
      data.reduce((sum, x) => sum + x.value, 0)
    )
    return {
      backgroundColor: CHART_SURFACE,
      animation: false,
      tooltip: {
        trigger: 'item',
        confine: true,
        valueFormatter: (v) => (typeof v === 'number' ? (visTickFormatter(v) ?? String(v)) : String(v ?? ''))
      },
      legend: { type: 'scroll', orient: 'horizontal', top: 0, textStyle: { fontSize: 10 } },
      series: [
        {
          type: 'pie',
          radius: ['0%', '70%'],
          center: ['50%', '56%'],
          data: data.map((d) => ({ name: d.name, value: d.value, itemStyle: fillStyleFor(identityFor(d.name), d.value / total) })),
          emphasis: { focus: 'self' },
          label: { show: false },
          labelLine: { show: false }
        }
      ]
    }
  }, [data])

  const onEvents = useMemo(
    () => ({
      click: (p: { name?: string }) => {
        if (onSliceClick && typeof p.name === 'string') onSliceClick(p.name)
      }
    }),
    [onSliceClick]
  )

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      onEvents={onEvents}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: height ?? 400, backgroundColor: CHART_SURFACE }}
    />
  )
}
